/**
 * RPM Claims Export Schema
 *
 * Defines the CSV export column configuration for RPM Claims table.
 * Maps claim data to CSV columns with proper formatting and transformations.
 */

import moment from 'moment-timezone';
import { UnifiedClaim } from '../../../interfaces/billing.rpm';
import { CSVColumn } from '../../../util/csvExport';
import { formatDateStringToMMDDYYYY } from '../../../util/dateFormat';

/**
 * Format date string to MM/DD/YYYY format for CSV
 */
function formatDateForCSV(dateString?: string): string {
  if (!dateString) return '';
  try {
    return formatDateStringToMMDDYYYY(dateString);
  } catch (error) {
    console.error('Error formatting date for CSV:', dateString, error);
    return dateString; // Return original if formatting fails
  }
}

/**
 * Format insurance company with ID
 */
function formatInsuranceForCSV(company: string, companyId: string): string {
  if (!company) return '';
  if (!companyId) return company;
  return `${company} (${companyId})`;
}

/**
 * CSV column definitions for RPM Claims export
 * Includes all relevant fields from the table and additional metadata
 */
export const RPM_CLAIMS_EXPORT_COLUMNS: CSVColumn<UnifiedClaim>[] = [
  {
    header: 'Claim ID',
    accessor: (claim) => claim?.id ?? 'N/A',
  },
  {
    header: 'Invoice ID',
    accessor: (claim) => claim?.invoiceId ?? 'N/A',
  },
  {
    header: 'Patient First Name',
    accessor: (claim) => claim?.billingMember?.firstName ?? 'N/A',
  },
  {
    header: 'Patient Last Name',
    accessor: (claim) => claim?.billingMember?.lastName ?? 'N/A',
  },
  {
    header: 'Patient Member UUID',
    accessor: (claim) => claim?.billingMember?.memberUuid ?? 'N/A',
  },
  {
    header: 'Member ID',
    accessor: (claim) => claim?.billingMember?.memberId ?? 'N/A',
  },
  {
    header: 'Clinic Name',
    accessor: (claim) => claim?.billingMember?.clinicName ?? 'N/A',
  },
  {
    header: 'Insurance Company',
    accessor: (claim) => {
      try {
        return formatInsuranceForCSV(
          claim?.billingMember?.insuranceCompany ?? '',
          claim?.billingMember?.insuranceCompanyId ?? ''
        );
      } catch (error) {
        console.error('Error formatting insurance for claim', claim?.id, error);
        return 'Error';
      }
    },
  },
  {
    header: 'Date of Service',
    accessor: (claim) => formatDateForCSV(claim?.dateOfService),
  },
  {
    header: 'Claim Created Date',
    accessor: (claim) => formatDateForCSV(claim?.createdAt),
  },
  {
    header: 'Process ID',
    accessor: (claim) => claim?.processId ?? 'N/A',
  },
  {
    header: 'Place of Service',
    accessor: (claim) => claim?.placeOfService ?? 'N/A',
  },
  {
    header: 'CPT Code Details',
    accessor: (claim) => {
      try {
        if (!claim?.cptCodes || claim.cptCodes.length === 0) return 'N/A';
        return claim.cptCodes
          .map(cpt => `${cpt?.cptCode ?? 'Unknown'} (${cpt?.units ?? 0} unit${cpt?.units !== 1 ? 's' : ''})`)
          .join('; ');
      } catch (error) {
        console.error('Error formatting CPT codes for claim', claim?.id, error);
        return 'Error';
      }
    },
  },
  {
    header: 'Status',
    accessor: (claim) => claim?.status ?? 'Unknown',
  },
  {
    header: 'Warnings Count',
    accessor: (claim) => claim?.warnings?.length ?? 0,
  },
  {
    header: 'Errors Count',
    accessor: (claim) => claim?.errors?.length ?? 0,
  },
  {
    header: 'Warning Messages',
    accessor: (claim) => {
      if (!claim?.warnings || claim.warnings.length === 0) return '';
      return claim.warnings.join(' | ');
    },
  },
  {
    header: 'Error Messages',
    accessor: (claim) => {
      if (!claim?.errors || claim.errors.length === 0) return '';
      return claim.errors.join(' | ');
    },
  },
  {
    header: 'Invoice Amount',
    accessor: (claim) => claim?.invoiceAmount ?? 'N/A',
  },
  {
    header: 'Coverage Type',
    accessor: (claim) => claim?.billingMember?.coverageType ?? 'N/A',
  },
  {
    header: 'Membership Type',
    accessor: (claim) => claim?.billingMember?.membershipType ?? 'N/A',
  },
  {
    header: 'Profile Status',
    accessor: (claim) => claim?.billingMember?.profileStatus ?? 'N/A',
  },
];

/**
 * Generate a filename for the CSV export based on current date/time and optional filters
 */
export function generateClaimsExportFilename(prefix: string = 'rpm-claims'): string {
  const timestamp = moment().format('YYYY-MM-DD_HHmmss');
  return `${prefix}_${timestamp}`;
}
